import React, { useCallback, useEffect, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Icon from '../../common/Icons';
import adminService from '../../../services/adminService';
import { AdminPersonnelPageHeader } from '../ui';
import { useApToast } from '../hooks/useApToast';
import { AP_COPY } from '../adminPersonnelCopy';
import { AP_PATHS } from '../adminPersonnelPaths';
import {
  mapActivityFromApi,
  formatActivityTime,
  getActivityIcon,
  getActivityNavigateTarget
} from '../utils/activityMappers';
import '../AdminPersonnelPortal.css';
import './AdminPersonnelActivitiesPage.css';

const PAGE_SIZE = 20;

const ENTITY_FILTERS = [
  { id: 'all', types: 'member,company,user', label: 'Tout' },
  { id: 'member', types: 'member', label: 'Adhérents' },
  { id: 'company', types: 'company', label: 'Sociétés' }
];

const ACTION_FILTERS = [
  { id: 'all', value: '', label: 'Toutes actions' },
  { id: 'create', value: 'create', label: 'Créations' },
  { id: 'update', value: 'update', label: 'Modifications' },
  { id: 'delete', value: 'delete', label: 'Suppressions' },
  { id: 'import', value: 'import', label: 'Imports' }
];

const DEFAULT_STATS = { total: 0, creates: 0, updates: 0, deletes: 0, imports: 0 };

export default function AdminPersonnelActivitiesPage() {
  const navigate = useNavigate();
  const { showToast } = useApToast();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState('audit_log');
  const [stats, setStats] = useState(DEFAULT_STATS);
  const [entityFilter, setEntityFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const offset = (page - 1) * PAGE_SIZE;

  const entityTypes = ENTITY_FILTERS.find((f) => f.id === entityFilter)?.types;
  const action = ACTION_FILTERS.find((f) => f.id === actionFilter)?.value;

  const loadActivities = useCallback(
    async (signal) => {
      try {
        setLoading(true);
        const response = await adminService.getRecentActivities(
          {
            limit: PAGE_SIZE,
            offset,
            entityTypes,
            action: action || undefined,
            withStats: true
          },
          { signal }
        );
        const rows = response?.data || [];
        setItems(rows.map(mapActivityFromApi));
        setTotal(response?.pagination?.total ?? rows.length);
        setSource(response?.source || 'audit_log');
        setStats(response?.stats || DEFAULT_STATS);
      } catch (error) {
        if (error?.name === 'AbortError') return;
        console.error('Chargement activités:', error);
        showToast('Impossible de charger les activités', 'danger');
        setItems([]);
        setTotal(0);
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [offset, entityTypes, action, showToast]
  );

  useEffect(() => {
    const controller = new AbortController();
    loadActivities(controller.signal);
    return () => controller.abort();
  }, [loadActivities]);

  useEffect(() => {
    setPage(1);
  }, [entityFilter, actionFilter]);

  const A = AP_COPY.ACTIVITIES;

  return (
    <div className="ap-dash ap-act-page">
      <AdminPersonnelPageHeader
        badge={AP_COPY.DASHBOARD.BADGE}
        title={A.PAGE_TITLE}
        description={A.PAGE_SUBTITLE}
        actions={
          <button
            type="button"
            className="ap-notif-btn ap-notif-btn--ghost"
            onClick={() => {
              const controller = new AbortController();
              loadActivities(controller.signal);
            }}
            disabled={loading}
          >
            <Icon name="refresh" size={14} />
            {A.BTN_REFRESH}
          </button>
        }
      />

      <section className="ap-shell">
        <div className="ap-act-stats">
          <div className="ap-act-stat">
            <span className="ap-act-stat__value">{stats.total}</span>
            <span className="ap-act-stat__label">{A.STAT_TOTAL}</span>
          </div>
          <div className="ap-act-stat ap-act-stat--create">
            <span className="ap-act-stat__value">{stats.creates}</span>
            <span className="ap-act-stat__label">{A.STAT_CREATES}</span>
          </div>
          <div className="ap-act-stat ap-act-stat--update">
            <span className="ap-act-stat__value">{stats.updates}</span>
            <span className="ap-act-stat__label">{A.STAT_UPDATES}</span>
          </div>
          <div className="ap-act-stat ap-act-stat--delete">
            <span className="ap-act-stat__value">{stats.deletes}</span>
            <span className="ap-act-stat__label">{A.STAT_DELETES}</span>
          </div>
          <div className="ap-act-stat ap-act-stat--import">
            <span className="ap-act-stat__value">{stats.imports}</span>
            <span className="ap-act-stat__label">{A.STAT_IMPORTS}</span>
          </div>
        </div>

        {source === 'legacy' && (
          <div className="ap-act-banner" role="status">
            <Icon name="infoCircle" size={16} />
            <span>{A.LEGACY_HINT}</span>
          </div>
        )}

        <div className="ap-toolbar ap-act-toolbar">
          <div className="ap-notif-filters" role="group" aria-label="Filtrer par entité">
            {ENTITY_FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`ap-notif-filter-pill ${entityFilter === f.id ? 'is-active' : ''}`}
                onClick={() => setEntityFilter(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <select
            className="form-select ap-toolbar-select ap-act-type-select"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            aria-label="Filtrer par action"
          >
            {ACTION_FILTERS.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label}
              </option>
            ))}
          </select>
        </div>

        <div className="ap-act-timeline-wrap">
          {loading ? (
            <div className="ap-act-state">
              <Spinner animation="border" variant="primary" size="sm" />
              <p>{A.LOADING}</p>
            </div>
          ) : items.length === 0 ? (
            <div className="ap-act-state">
              <div className="ap-act-state__icon">
                <Icon name="history" size={28} />
              </div>
              <h3>{A.EMPTY_TITLE}</h3>
              <p>{A.EMPTY_SUB}</p>
              <div className="ap-act-state__actions">
                <Button
                  variant="primary"
                  className="rounded-3"
                  onClick={() => navigate(AP_PATHS.COMPANIES)}
                >
                  {A.GO_COMPANIES}
                </Button>
                <Button
                  variant="outline-primary"
                  className="rounded-3"
                  onClick={() => navigate(AP_PATHS.MEMBERS)}
                >
                  {A.GO_MEMBERS}
                </Button>
              </div>
            </div>
          ) : (
            <ol className="ap-act-timeline">
              {items.map((activity, index) => {
                const icon = getActivityIcon(activity.uiType);
                const navTarget = getActivityNavigateTarget(activity);

                return (
                  <li key={activity.id} className="ap-act-item">
                    {index < items.length - 1 && <span className="ap-act-item__line" aria-hidden />}
                    <span className={`ap-act-item__icon ap-act-item__icon--${activity.uiType}`}>
                      <Icon name={icon} size={18} />
                    </span>
                    <div className="ap-act-item__body">
                      <div className="ap-act-item__head">
                        <div className="ap-act-item__tags">
                          <span className="ap-act-tag ap-act-tag--entity">{activity.entityLabel}</span>
                          <span className={`ap-act-tag ap-act-tag--action ap-act-tag--${activity.action}`}>
                            {activity.actionLabel}
                          </span>
                        </div>
                        <time dateTime={activity.timestamp}>{formatActivityTime(activity.timestamp)}</time>
                      </div>
                      <h4 className="ap-act-item__title">{activity.title}</h4>
                      {activity.description && (
                        <p className="ap-act-item__desc">{activity.description}</p>
                      )}
                      <div className="ap-act-item__footer">
                        {activity.user && (
                          <span className="ap-act-item__actor">
                            <Icon name="user" size={12} />
                            {activity.user}
                          </span>
                        )}
                        {navTarget && (
                          <button
                            type="button"
                            className="ap-notif-link-btn"
                            onClick={() => navigate(navTarget)}
                          >
                            {activity.entityType === 'company' ? A.GO_COMPANIES : A.GO_MEMBERS}
                            <Icon name="chevronRight" size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        {!loading && total > 0 && (
          <footer className="ap-mem-footer ap-act-footer">
            <span>{A.PAGINATION_LABEL(offset + 1, Math.min(offset + PAGE_SIZE, total), total)}</span>
            {totalPages > 1 && (
              <nav className="ap-mem-page-nav" aria-label="Pagination activités">
                <button
                  type="button"
                  className="ap-mem-page-btn"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <Icon name="chevronLeft" size={14} />
                </button>
                <span className="ap-mem-page-label">
                  {page} / {totalPages}
                </span>
                <button
                  type="button"
                  className="ap-mem-page-btn"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  <Icon name="chevronRight" size={14} />
                </button>
              </nav>
            )}
          </footer>
        )}
      </section>
    </div>
  );
}
